import React, { useCallback, useRef, useMemo } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { useTheme } from 'next-themes';
import { sql, MSSQL, MySQL, PostgreSQL, StandardSQL } from '@codemirror/lang-sql';
import { javascript } from '@codemirror/lang-javascript';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import { html } from '@codemirror/lang-html';
import { graphql as graphqlLang } from 'cm6-graphql';
import { buildSchema, GraphQLSchema } from 'graphql';
import { autocompletion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';
import { EditorView } from '@codemirror/view';
import { useDrop } from 'react-dnd';
import { DragItem } from '@/contexts/DragDropContext';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ExpressionGenerator } from '@/lib/expression-generator';
import { Button } from '@/components/ui/button';
import { Wand2, Sparkles } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export type EditorLanguage = 'sql' | 'javascript' | 'json' | 'python' | 'html' | 'graphql' | 'plaintext';
export type SqlDialect = 'mssql' | 'mysql' | 'postgresql' | 'standard';

interface InputData {
  nodeId: string;
  nodeName: string;
  nodeType?: string;
  nodeDistance: number;
  data: any;
  sourceHandle?: string;
  isVirtualItem?: boolean;
  virtualItemLabel?: string;
}

interface CodeMirrorEditorProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  onFocus?: () => void;
  language?: EditorLanguage;
  sqlDialect?: SqlDialect;
  height?: string;
  placeholder?: string;
  className?: string;
  showFormatButton?: boolean;
  readOnly?: boolean;
  lineNumbers?: boolean;
  // AI Help props
  showAIHelp?: boolean;
  onOpenAIHelp?: () => void; // Callback to open AI Help tab (for Code nodes)
  inputData?: InputData[];
  executionTier?: 'sandboxed' | 'extended';
  // GraphQL schema for autocomplete (optional - enables schema-aware completions)
  graphqlSchema?: GraphQLSchema | string;
}

// SQL keywords for autocomplete
const sqlKeywords = [
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'IN', 'BETWEEN', 'LIKE', 'IS', 'NULL',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'CREATE', 'TABLE', 'DROP', 'ALTER',
  'JOIN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'FULL', 'CROSS', 'ON', 'AS',
  'ORDER', 'BY', 'ASC', 'DESC', 'GROUP', 'HAVING', 'LIMIT', 'OFFSET', 'TOP',
  'DISTINCT', 'ALL', 'UNION', 'INTERSECT', 'EXCEPT',
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'COALESCE', 'NULLIF', 'CAST', 'CONVERT',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'INDEX', 'UNIQUE', 'CHECK', 'DEFAULT', 'CONSTRAINT',
  'BEGIN', 'COMMIT', 'ROLLBACK', 'TRANSACTION',
];

// Custom SQL completions
function sqlCompletions(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/\w*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  return {
    from: word.from,
    options: [
      ...sqlKeywords.map(kw => ({
        label: kw,
        type: 'keyword',
      })),
      {
        label: '{{ $json.field }}',
        type: 'variable',
        info: 'Reference input JSON field',
      },
      {
        label: '{{ $node["NodeName"].field }}',
        type: 'variable',
        info: 'Reference another node output',
      },
    ],
  };
}

// JavaScript/workflow completions
function jsCompletions(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[\w$]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  return {
    from: word.from,
    options: [
      { label: '$json', type: 'variable', info: 'Current node JSON data' },
      { label: '$input', type: 'variable', info: 'Input data from previous node' },
      { label: '$node', type: 'variable', info: 'Reference to another node' },
      { label: '$env', type: 'variable', info: 'Environment variables' },
      { label: '$items', type: 'variable', info: 'All input items' },
      { label: 'console.log', type: 'function', info: 'Log to console' },
      { label: 'JSON.stringify', type: 'function', info: 'Convert to JSON string' },
      { label: 'JSON.parse', type: 'function', info: 'Parse JSON string' },
    ],
  };
}

// GraphQL keywords for autocomplete
const graphqlKeywords = [
  // Operations
  'query', 'mutation', 'subscription', 'fragment', 'on',
  // Type system
  'type', 'interface', 'union', 'enum', 'input', 'scalar', 'extend', 'schema',
  // Field modifiers
  'implements', 'directive',
  // Built-in scalars
  'Int', 'Float', 'String', 'Boolean', 'ID',
  // Directives
  '@skip', '@include', '@deprecated',
  // Structural
  'true', 'false', 'null',
];

// GraphQL completions - provides keywords and common patterns
function graphqlCompletions(context: CompletionContext): CompletionResult | null {
  const word = context.matchBefore(/[@\w]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  return {
    from: word.from,
    options: [
      ...graphqlKeywords.map(kw => ({
        label: kw,
        type: kw.startsWith('@') ? 'keyword' : (kw[0] === kw[0].toUpperCase() ? 'type' : 'keyword'),
        boost: kw.startsWith('@') ? 1 : (kw === 'query' || kw === 'mutation' ? 2 : 0),
      })),
      // Common GraphQL patterns
      { label: 'pageInfo', type: 'property', info: 'Pagination info (hasNextPage, endCursor)' },
      { label: 'items', type: 'property', info: 'List of items in connection' },
      { label: 'edges', type: 'property', info: 'Connection edges with cursor' },
      { label: 'node', type: 'property', info: 'Node in an edge' },
      { label: 'hasNextPage', type: 'property', info: 'More pages available' },
      { label: 'hasPreviousPage', type: 'property', info: 'Previous pages available' },
      { label: 'endCursor', type: 'property', info: 'Cursor for next page' },
      { label: 'startCursor', type: 'property', info: 'Cursor for previous page' },
      { label: 'totalCount', type: 'property', info: 'Total number of items' },
      // Result types
      { label: '... on Succeeded', type: 'text', info: 'Success result fragment' },
      { label: '... on FailedValidation', type: 'text', info: 'Validation error fragment' },
      { label: '... on UnexpectedError', type: 'text', info: 'Unexpected error fragment' },
      // Variables
      { label: '$first', type: 'variable', info: 'Pagination: number of items' },
      { label: '$after', type: 'variable', info: 'Pagination: cursor for next page' },
      { label: '$filter', type: 'variable', info: 'Filter input' },
    ],
  };
}

// Simple SQL formatter
const formatSQL = (sqlText: string): string => {
  const newLineKeywords = [
    'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN',
    'INNER JOIN', 'OUTER JOIN', 'FULL JOIN', 'CROSS JOIN', 'ON', 'GROUP BY',
    'HAVING', 'ORDER BY', 'LIMIT', 'OFFSET', 'UNION', 'INTERSECT', 'EXCEPT',
    'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM',
  ];

  let formatted = sqlText.replace(/\s+/g, ' ').trim();

  newLineKeywords.forEach(keyword => {
    const regex = new RegExp(`\\s+(${keyword})\\s+`, 'gi');
    formatted = formatted.replace(regex, `\n$1 `);
  });

  formatted = formatted.replace(/^SELECT\s+/i, 'SELECT ');

  const lines = formatted.split('\n');
  const result: string[] = [];

  lines.forEach((line) => {
    const trimmedLine = line.trim();
    const upperLine = trimmedLine.toUpperCase();
    const isSubClause = upperLine.startsWith('AND') || upperLine.startsWith('OR');

    if (isSubClause) {
      result.push('  ' + trimmedLine);
    } else {
      result.push(trimmedLine);
    }
  });

  return result.join('\n');
};

// Simple JSON formatter
const formatJSON = (jsonText: string): string => {
  // Handle template variables by temporarily replacing them
  const templateVars: string[] = [];
  let processed = jsonText.replace(/\{\{[^}]+\}\}/g, (match) => {
    templateVars.push(match);
    return `"__TEMPLATE_${templateVars.length - 1}__"`;
  });

  try {
    const parsed = JSON.parse(processed);
    let formatted = JSON.stringify(parsed, null, 2);

    // Restore template variables
    templateVars.forEach((tv, i) => {
      formatted = formatted.replace(`"__TEMPLATE_${i}__"`, tv);
    });

    return formatted;
  } catch {
    // If JSON is invalid, just return original
    return jsonText;
  }
};

export const CodeMirrorEditor: React.FC<CodeMirrorEditorProps> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  language = 'plaintext',
  sqlDialect = 'standard',
  height = '200px',
  placeholder = 'Enter code...',
  className,
  showFormatButton = true,
  readOnly = false,
  lineNumbers = true,
  showAIHelp = false,
  onOpenAIHelp,
  inputData = [],
  executionTier = 'sandboxed',
  graphqlSchema,
}) => {
  const editorRef = useRef<ReactCodeMirrorRef>(null);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';

  // Get SQL dialect configuration
  const getSqlDialect = useCallback(() => {
    switch (sqlDialect) {
      case 'mssql':
        return MSSQL;
      case 'mysql':
        return MySQL;
      case 'postgresql':
        return PostgreSQL;
      default:
        return StandardSQL;
    }
  }, [sqlDialect]);

  // Build extensions based on language
  const extensions = useMemo(() => {
    const exts = [EditorView.lineWrapping];

    switch (language) {
      case 'sql':
        exts.push(sql({ dialect: getSqlDialect() }));
        exts.push(autocompletion({ override: [sqlCompletions] }));
        break;
      case 'javascript':
        exts.push(javascript());
        exts.push(autocompletion({ override: [jsCompletions] }));
        break;
      case 'json':
        exts.push(json());
        break;
      case 'python':
        exts.push(python());
        break;
      case 'html':
        exts.push(html());
        break;
      case 'graphql':
        // If a schema is provided (string or GraphQLSchema), use it for autocomplete
        if (graphqlSchema) {
          try {
            const schema = typeof graphqlSchema === 'string'
              ? buildSchema(graphqlSchema)
              : graphqlSchema;
            exts.push(graphqlLang(schema));
          } catch (e) {
            // If schema parsing fails, fall back to basic GraphQL support
            console.warn('Failed to parse GraphQL schema for autocomplete:', e);
            exts.push(graphqlLang());
            exts.push(autocompletion({ override: [graphqlCompletions] }));
          }
        } else {
          // No schema - use basic keyword completions
          exts.push(graphqlLang());
          exts.push(autocompletion({ override: [graphqlCompletions] }));
        }
        break;
      // plaintext - no language extension needed
    }

    return exts;
  }, [language, getSqlDialect, graphqlSchema]);

  // Handle drag and drop
  const [{ isOver }, drop] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: ['variable', 'schema-field'],
    drop: (item) => {
      let insertText = '';
      if (item.type === 'schema-field') {
        // Use centralized ExpressionGenerator to handle proper bracket notation
        // for property names with spaces (e.g., "AI Agent" -> ["AI Agent"])
        insertText = ExpressionGenerator.generate(item);
      } else if (item.type === 'variable') {
        insertText = item.label || '';
      }

      const view = editorRef.current?.view;
      if (view) {
        const { from, to } = view.state.selection.main;
        view.dispatch({
          changes: { from, to, insert: insertText },
          selection: { anchor: from + insertText.length },
        });
        view.focus();
        toast.success(`Inserted ${item.type === 'schema-field' ? item.path : item.label}`);
      } else {
        onChange(value + insertText);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Format handler
  const handleFormat = useCallback(() => {
    try {
      let formatted: string;

      switch (language) {
        case 'sql':
          formatted = formatSQL(value);
          break;
        case 'json':
          formatted = formatJSON(value);
          break;
        default:
          // For JS/Python, just normalize whitespace for now
          formatted = value;
          toast.info('Auto-format not available for this language');
          return;
      }

      onChange(formatted);
      toast.success(`${language.toUpperCase()} formatted successfully`);
    } catch (error) {
      toast.error(`Failed to format ${language.toUpperCase()}`);
    }
  }, [value, onChange, language]);

  // Handle blur
  const handleBlur = useCallback(() => {
    if (onBlur) {
      onBlur(value);
    }
  }, [onBlur, value]);

  // Handle focus
  const handleFocus = useCallback(() => {
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  // Should show format button?
  const canFormat = language === 'sql' || language === 'json';

  // Should show AI help button? For JavaScript, Python, and HTML with a callback
  const canShowAIHelp = showAIHelp && onOpenAIHelp && (language === 'javascript' || language === 'python' || language === 'html') && !readOnly;

  return (
    <div className={cn('flex flex-col gap-2 min-w-0 w-full', className)}>
      {/* Toolbar buttons */}
      {((showFormatButton && canFormat && !readOnly) || canShowAIHelp) && (
        <div className="flex items-center justify-end gap-2">
          {/* AI Help button */}
          {canShowAIHelp && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={onOpenAIHelp}
                    className="h-7 px-2"
                  >
                    <Sparkles className="h-3 w-3 mr-1" />
                    AI Help
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Get AI assistance with {language === 'html' ? 'HTML template' : `${language} code`}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* Format button */}
          {showFormatButton && canFormat && !readOnly && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleFormat}
                    className="h-7 px-2"
                  >
                    <Wand2 className="h-3 w-3 mr-1" />
                    Format
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Format {language.toUpperCase()}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}

      {/* CodeMirror Editor */}
      <div
        ref={drop as any}
        className={cn(
          'border rounded-md overflow-hidden min-w-0 w-full',
          isOver && 'ring-2 ring-primary border-primary bg-primary/5'
        )}
      >
        <CodeMirror
          ref={editorRef}
          value={value || ''}
          height={height}
          theme={isDarkMode ? 'dark' : 'light'}
          extensions={extensions}
          onChange={onChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          readOnly={readOnly}
          placeholder={placeholder}
          basicSetup={{
            lineNumbers: lineNumbers,
            highlightActiveLineGutter: true,
            highlightActiveLine: true,
            foldGutter: false,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: false,
            highlightSelectionMatches: true,
            tabSize: 2,
          }}
          style={{
            fontSize: '13px',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        />
      </div>
    </div>
  );
};

export default CodeMirrorEditor;
